import React, { useEffect, useState } from 'react';
import { Badge, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import ModalHelp from '../../../components/ModalHelp';
import { toast } from 'react-toastify';
import ContaServicoViewModel, { PerfilContaServico } from '../../../viewmodel/ContaServicoViewModel';
import { Permissao } from '../../../model/Usuario/Permissao';

type PerfilFormState = {
  nome: string;
  descricao: string;
  permissaoIds: string[];
};

const PerfisContaServico: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [perfis, setPerfis] = useState<PerfilContaServico[]>([]);
  const [permissoesDisponiveis, setPermissoesDisponiveis] = useState<Permissao[]>([]);
  const [perfilEditando, setPerfilEditando] = useState<PerfilContaServico | null>(null);
  const [form, setForm] = useState<PerfilFormState>({
    nome: '',
    descricao: '',
    permissaoIds: []
  });

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      const [perfisData, permissoesData] = await Promise.all([
        ContaServicoViewModel.getPerfisContaServico(),
        ContaServicoViewModel.getPermissoesPerfisContaServico()
      ]);
      setPerfis(perfisData || []);
      setPermissoesDisponiveis(permissoesData || []);
    } catch {
      toast.error('Erro ao carregar perfis de conta de serviço');
    } finally {
      setLoading(false);
    }
  };

  const abrirCriacao = () => {
    setPerfilEditando(null);
    setForm({ nome: '', descricao: '', permissaoIds: [] });
    setShowModal(true);
  };

  const abrirEdicao = (perfil: PerfilContaServico) => {
    setPerfilEditando(perfil);
    setForm({
      nome: perfil.nome,
      descricao: perfil.descricao,
      permissaoIds: (perfil.permissoes || []).map((p) => p.id!).filter(Boolean)
    });
    setShowModal(true);
  };

  const alternarPermissao = (permissaoId: string) => {
    setForm((prev) => ({
      ...prev,
      permissaoIds: prev.permissaoIds.includes(permissaoId)
        ? prev.permissaoIds.filter((id) => id !== permissaoId)
        : [...prev.permissaoIds, permissaoId]
    }));
  };

  const salvar = async () => {
    if (!form.nome.trim()) {
      toast.error('Informe o nome do perfil');
      return;
    }
    if (!form.descricao.trim()) {
      toast.error('Informe a descrição do perfil');
      return;
    }

    const permissoesSelecionadas = permissoesDisponiveis.filter((p) => p.id && form.permissaoIds.includes(p.id));
    if (permissoesSelecionadas.length === 0) {
      toast.error('Selecione ao menos uma permissão');
      return;
    }

    try {
      setSaving(true);
      const payload: PerfilContaServico = {
        nome: form.nome.trim(),
        descricao: form.descricao.trim(),
        permissoes: permissoesSelecionadas
      };

      if (perfilEditando?.id) {
        await ContaServicoViewModel.updatePerfilContaServico(perfilEditando.id, payload);
        toast.success('Perfil de conta de serviço atualizado com sucesso');
      } else {
        await ContaServicoViewModel.createPerfilContaServico(payload);
        toast.success('Perfil de conta de serviço criado com sucesso');
      }

      setShowModal(false);
      await carregarDados();
    } catch {
      toast.error('Falha ao salvar perfil de conta de serviço');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <Button variant="success" onClick={abrirCriacao}>
          + Novo Perfil
        </Button>
      </div>

      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Descrição</th>
              <th>Permissões</th>
              <th style={{ width: 120 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {perfis.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-muted py-4">
                  Nenhum perfil de conta de serviço cadastrado.
                </td>
              </tr>
            ) : (
              perfis.map((perfil) => (
                <tr key={perfil.id}>
                  <td>{perfil.nome}</td>
                  <td>{perfil.descricao}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {(perfil.permissoes || []).map((p) => (
                        <Badge bg="secondary" key={`${perfil.id}-${p.id || p.nome}`}>
                          {p.nome}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td>
                    <Button size="sm" variant="outline-primary" onClick={() => abrirEdicao(perfil)}>
                      Editar
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {perfilEditando ? 'Editar Perfil de Conta de Serviço' : 'Criar Perfil de Conta de Serviço'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ModalHelp text="Defina o nome do perfil (ex: INTEGRACAO_CANDIDATOS) e selecione as permissões que ele terá. Cada conta de serviço recebe um perfil que determina quais endpoints da API ela pode acessar." />
          <Form.Group className="mb-3">
            <Form.Label>Nome</Form.Label>
            <Form.Control
              value={form.nome}
              onChange={(e) => setForm((prev) => ({ ...prev, nome: e.target.value }))}
              placeholder="Ex.: INTEGRACAO_CANDIDATOS"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Descrição</Form.Label>
            <Form.Control
              value={form.descricao}
              onChange={(e) => setForm((prev) => ({ ...prev, descricao: e.target.value }))}
              placeholder="Descrição do perfil"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Permissões</Form.Label>
            <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6, padding: 12 }}>
              {permissoesDisponiveis.map((permissao) => (
                <Form.Check
                  key={permissao.id}
                  id={`permissao-${permissao.id}`}
                  type="checkbox"
                  label={`${permissao.nome}${permissao.descricao ? ` - ${permissao.descricao}` : ''}`}
                  checked={!!(permissao.id && form.permissaoIds.includes(permissao.id))}
                  onChange={() => permissao.id && alternarPermissao(permissao.id)}
                  className="mb-2"
                />
              ))}
            </div>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={salvar} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PerfisContaServico;
